import React, { useEffect, useState } from 'react';
import { MapPin, MessageCircle, Instagram, Loader2 } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import { siteConfig } from '../mock/data';
import { trackClick } from '../lib/tracking';
import { fetchBranches } from '../api/services';
import { useSettings } from '../context/SettingsContext';
import SectionRenderer from '../components/page-builder/SectionRenderer';
import { getPage } from '../lib/pageDefaults';

export default function Branches() {
  const { settings } = useSettings();
  const page = getPage(settings?.page_builder, '/agen') || {};
  const c = page.content || {};
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchBranches()
      .then((data) => { if (alive) setBranches(data); })
      .catch(() => { if (alive) setBranches([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <>
      <PageBanner
        title={page.title || "Agen & Toko Cabang"}
        subtitle={page.subtitle || "Kunjungi toko cabang atau hubungi agen resmi BelBel terdekat di kota Anda."}
        breadcrumb={['Agen & Cabang']}
      />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat cabang...
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {branches.map((b) => (
                <div key={b.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 card-shadow">
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img src={b.image} alt={b.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs font-bold text-japo-yellow uppercase tracking-widest mb-1">{b.region}</div>
                    <h3 className="font-bold text-xl text-gray-900 mb-3">{b.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5 flex gap-2">
                      <MapPin className="w-4 h-4 text-japo-green flex-shrink-0 mt-0.5" />
                      <span>{b.address}</span>
                    </p>
                    <div className="flex gap-2">
                      <a
                        href={b.map_link || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                      >
                        <MapPin className="w-4 h-4" />
                        Lihat Lokasi
                      </a>
                      <a
                        href={`https://wa.me/${b.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackClick('whatsapp_branch', { branch: b.title })}
                        className="inline-flex items-center justify-center bg-[#25D366] hover:bg-[#1ebe57] text-white p-2.5 rounded-lg transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </a>
                      <a
                        href={siteConfig.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center bg-pink-500 hover:bg-pink-600 text-white p-2.5 rounded-lg transition-colors"
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-16 bg-gradient-to-r from-japo-green to-japo-green-dark rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-japo-yellow/20 blur-3xl"></div>
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-japo-yellow font-bold text-sm uppercase tracking-widest mb-3">Peluang Kemitraan</div>
                <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
                  {c.ctaTitle || 'Jadilah Agen BelBel di Kota Anda'}
                </h3>
                <p className="text-white/85 leading-relaxed">
                  {c.ctaDescription || 'Bergabunglah dengan ribuan agen sukses BelBel. Nikmati harga khusus, dukungan promosi, dan komitmen jangka panjang.'}
                </p>
              </div>
              <div className="flex md:justify-end">
                <a
                  href={`https://wa.me/${settings.whatsapp_number || siteConfig.whatsappNumber}?text=${encodeURIComponent('Halo Kak! Saya tertarik menjadi agen BelBel. Boleh info detailnya?')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick('whatsapp_agent_cta')}
                  className="inline-flex items-center gap-2 bg-japo-yellow hover:bg-[#D19400] text-gray-900 font-extrabold px-8 py-4 rounded-xl shadow-xl transition-all"
                >
                  <MessageCircle className="w-5 h-5" />
                  {c.ctaButton || 'Daftar Sekarang'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionRenderer sections={page.sections || []} />
    </>
  );
}
